import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import { useTheme, alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';

// Icons
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import CategoryIcon from '@mui/icons-material/Category';
import MessageIcon from '@mui/icons-material/Message';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Custom components
import GridContainer from '../components/common/GridContainer';
import GridItem from '../components/common/GridItem';
import ResponsiveBox from '../components/common/ResponsiveBox';
import SEO from '../components/common/SEO';
import { pageSEO, generateStructuredData } from '../utils/seo';

// Form interface
interface ContactFormValues {
  name: string;
  email: string;
  phone: string;
  inquiryType: string;
  message: string;
  subscribe: boolean;
}

// Inquiry types
const inquiryTypes = [
  {
    value: 'corporate-account',
    label: 'Corporate Account Inquiry'
  },
  {
    value: 'booking-assistance',
    label: 'Booking Assistance'
  },
  {
    value: 'fleet-information',
    label: 'Fleet Information'
  },
  {
    value: 'feedback',
    label: 'Feedback'
  },
  {
    value: 'other',
    label: 'Other'
  }
];

const ContactPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const seoData = pageSEO['/contact'];
  const structuredData = generateStructuredData('/contact');
  
  // Form state
  const [formValues, setFormValues] = useState<ContactFormValues>({
    name: '',
    email: '',
    phone: '',
    inquiryType: '',
    message: '',
    subscribe: false
  });
  
  // Field validation state
  const [fieldErrors, setFieldErrors] = useState({
    name: false,
    email: false,
    inquiryType: false,
    message: false
  });
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [formTouched, setFormTouched] = useState(false);
  
  // Handle form changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    const val = name === 'subscribe' ? checked : value;
    
    setFormValues({
      ...formValues,
      [name]: val
    });
    
    if (!formTouched) setFormTouched(true);
    
    // Clear error when field is edited
    if (fieldErrors[name as keyof typeof fieldErrors]) {
      setFieldErrors({
        ...fieldErrors,
        [name]: false
      });
    }
  };
  
  // Basic validation
  const validateForm = (): boolean => {
    const errors = {
      name: !formValues.name.trim(),
      email: !formValues.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email),
      inquiryType: !formValues.inquiryType,
      message: !formValues.message.trim()
    };
    
    setFieldErrors(errors);
    return !Object.values(errors).some(error => error);
  };
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccess(true);
      // Reset form
      setFormValues({
        name: '',
        email: '',
        phone: '',
        inquiryType: '',
        message: '',
        subscribe: false
      });
      setFormTouched(false);
    }, 1500);
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };
  
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <>
      <SEO 
        pathname="/contact" 
        {...seoData} 
        structuredData={structuredData}
      />
      <Box
        sx={{
        minHeight: '100vh',
        background: `
          linear-gradient(135deg, 
            ${alpha(theme.palette.primary.main, 0.05)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.05)} 100%
          ),
          radial-gradient(
            circle at 50% 50%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 0% 100%,
            ${alpha(theme.palette.secondary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 100% 0%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          )
        `,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(
              circle at 50% 50%,
              ${alpha(theme.palette.primary.main, 0.03)} 0%,
              transparent 70%
            )
          `,
          animation: 'pulse 8s ease-in-out infinite',
          '@keyframes pulse': {
            '0%': {
              transform: 'scale(1)',
              opacity: 1,
            },
            '50%': {
              transform: 'scale(1.2)',
              opacity: 0.8,
            },
            '100%': {
              transform: 'scale(1)',
              opacity: 1,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(
              45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
            ),
            linear-gradient(
              -45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
          )`,
          backgroundSize: '60px 60px',
          opacity: 0.5,
          animation: 'move 20s linear infinite',
          '@keyframes move': {
            '0%': {
              backgroundPosition: '0 0',
            },
            '100%': {
              backgroundPosition: '60px 60px',
            },
          },
          zIndex: 0,
        }}
      />
      {/* Hero Section with Background */}
      <ResponsiveBox
        sx={{
          position: 'relative',
          color: 'white',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          zIndex: 1,
        }}
        responsiveHeight={{
          xs: 250,
          sm: 300,
          md: 400
        }}
      >
        {/* Background image with overlay */}
        <Box 
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: -1,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: `linear-gradient(to right, ${alpha(theme.palette.primary.dark, 0.9)} 0%, ${alpha(theme.palette.primary.main, 0.8)} 100%)`,
              zIndex: 1
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '30%',
              background: `linear-gradient(to top, ${theme.palette.primary.dark} 0%, transparent 100%)`,
              zIndex: 2
            }
          }}
        >
          <Box
            component="img"
            src="/images/contact-hero.jpg"
            alt="Contact Us"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              filter: 'brightness(0.7)',
            }}
          />
        </Box>
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom
              sx={{
                fontWeight: 700,
                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                position: 'relative',
                fontSize: {
                  xs: '2rem',
                  sm: '2.5rem',
                  md: '3.5rem'
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -10,
                  left: 0,
                  width: 80,
                  height: 4,
                  backgroundColor: theme.palette.secondary.main,
                  borderRadius: 2,
                }
              }}
            >
              Contact Us
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                maxWidth: 800,
                fontWeight: 400,
                textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                mb: 2,
                fontSize: {
                  xs: '1rem',
                  sm: '1.25rem',
                  md: '1.5rem'
                }
              }}
            >
              Get in touch with our team for inquiries, booking assistance, or to set up a corporate account
            </Typography>
            
            <ResponsiveBox
              sx={{ display: 'flex', alignItems: 'center', mt: 3 }}
              responsiveDirection={{
                xs: 'column',
                sm: 'row'
              }}
            >
              <Box 
                sx={{ 
                  bgcolor: alpha(theme.palette.secondary.main, 0.9),
                  borderRadius: 10,
                  py: 0.5,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  mb: { xs: 2, sm: 0 },
                  mr: { sm: 2 }
                }}
              >
                <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body2" fontWeight={600}>
                  24/7 Support: +91 6351003667
                </Typography>
              </Box>

              <Button
                variant="outlined"
                color="inherit"
                sx={{ 
                  borderRadius: 10,
                  borderWidth: 2,
                  px: 2,
                  borderColor: 'white',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: alpha(theme.palette.common.white, 0.1),
                  }
                }}
                endIcon={<ArrowForwardIcon />}
                href="#contactForm"
              >
                Get in Touch
              </Button>
            </ResponsiveBox>
          </motion.div>
        </Container>
      </ResponsiveBox>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 }, position: 'relative', zIndex: 1 }}>
        <GridContainer spacing={{ xs: 3, md: 5 }}>
          {/* Contact Form */}
          <GridItem xs={12} md={7}>
            <Box id="contactForm">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeInUp}
                transition={{ duration: 0.5 }}
              >
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: { xs: 2, sm: 3, md: 5 }, 
                    borderRadius: { xs: 2, md: 4 },
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    boxShadow: '0 10px 40px rgba(0,0,0,0.06)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* Decorative element */}
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      top: -50,
                      right: -50,
                      width: 200,
                      height: 200,
                      borderRadius: '50%',
                      background: `radial-gradient(circle, ${alpha(theme.palette.secondary.light, 0.1)} 0%, ${alpha(theme.palette.secondary.light, 0)} 70%)`,
                      zIndex: 0
                    }}
                  />
                  
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography 
                      variant="h4" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        mb: 1,
                        fontSize: { xs: '1.75rem', md: '2.125rem' }
                      }}
                    >
                      Send Us a Message
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="text.secondary"
                      sx={{ mb: 4 }}
                    >
                      Fill out the form below and our team will respond to your inquiry as soon as possible.
                    </Typography>
                    
                    <form onSubmit={handleSubmit}>
                      <motion.div variants={staggerContainer} initial="hidden" animate="visible">
                        <GridContainer spacing={{ xs: 2, md: 3 }}>
                          <GridItem xs={12} sm={6}>
                            <motion.div variants={fadeInUp}>
                              <TextField
                                required
                                fullWidth
                                label="Full Name"
                                name="name"
                                value={formValues.name}
                                onChange={handleChange}
                                error={fieldErrors.name}
                                helperText={fieldErrors.name ? "Please enter your name" : ""}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <PersonOutlineIcon color="primary" />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2
                                  }
                                }}
                              />
                            </motion.div>
                          </GridItem>
                          <GridItem xs={12} sm={6}>
                            <motion.div variants={fadeInUp}>
                              <TextField
                                required
                                fullWidth
                                label="Email Address"
                                name="email"
                                type="email"
                                value={formValues.email}
                                onChange={handleChange}
                                error={fieldErrors.email}
                                helperText={fieldErrors.email ? "Please enter a valid email address" : ""}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <AlternateEmailIcon color="primary" />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2
                                  }
                                }}
                              />
                            </motion.div>
                          </GridItem>
                          <GridItem xs={12} sm={6}>
                            <motion.div variants={fadeInUp}>
                              <TextField
                                fullWidth
                                label="Phone Number"
                                name="phone"
                                value={formValues.phone}
                                onChange={handleChange}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <PhoneIcon color="primary" />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2
                                  }
                                }}
                              />
                            </motion.div>
                          </GridItem>
                          <GridItem xs={12} sm={6}>
                            <motion.div variants={fadeInUp}>
                              <TextField
                                required
                                fullWidth
                                select
                                label="Inquiry Type"
                                name="inquiryType"
                                value={formValues.inquiryType}
                                onChange={handleChange}
                                error={fieldErrors.inquiryType}
                                helperText={fieldErrors.inquiryType ? "Please select an inquiry type" : ""}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <CategoryIcon color="primary" />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2
                                  }
                                }}
                              >
                                {inquiryTypes.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </motion.div>
                          </GridItem>
                          <GridItem xs={12}>
                            <motion.div variants={fadeInUp}>
                              <TextField
                                required
                                fullWidth
                                multiline
                                rows={4}
                                label="Your Message"
                                name="message"
                                value={formValues.message}
                                onChange={handleChange}
                                error={fieldErrors.message}
                                helperText={fieldErrors.message ? "Please enter your message" : ""}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                                      <MessageIcon color="primary" />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2
                                  }
                                }}
                              />
                            </motion.div>
                          </GridItem>
                          <GridItem xs={12}>
                            <motion.div variants={fadeInUp}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={formValues.subscribe}
                                    onChange={handleChange}
                                    name="subscribe"
                                    color="primary"
                                  />
                                }
                                label={
                                  <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                                    Subscribe to our newsletter for updates and special offers
                                  </Typography>
                                }
                              />
                            </motion.div>
                          </GridItem>
                          <GridItem xs={12} sx={{ 
                            display: 'flex', 
                            justifyContent: { xs: 'center', sm: 'flex-end' }, 
                            mt: { xs: 1, md: 2 } 
                          }}>
                            <motion.div 
                              variants={fadeInUp}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                size={isMobile ? "medium" : "large"}
                                disabled={isSubmitting}
                                endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                sx={{
                                  py: { xs: 1, md: 1.5 },
                                  px: { xs: 3, md: 4 },
                                  borderRadius: 30,
                                  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)',
                                  '&:hover': {
                                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                                  },
                                  width: { xs: '100%', sm: 'auto' }
                                }}
                              >
                                {isSubmitting ? 'Submitting...' : 'Send Message'}
                              </Button>
                            </motion.div>
                          </GridItem>
                        </GridContainer>
                      </motion.div>
                    </form>
                  </Box>
                </Paper>
              </motion.div>
            </Box>
          </GridItem>
          
          {/* Company Info */}
          <GridItem xs={12} md={5}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              {/* Corporate Headquarters Card */}
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: { xs: 2, md: 4 },
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
                  position: 'relative',
                  overflow: 'hidden',
                  mb: { xs: 3, md: 4 },
                  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
                }}
              >
                {/* Decorative element */}
                <Box 
                  sx={{ 
                    position: 'absolute',
                    top: -80,
                    right: -80,
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.08)} 0%, ${alpha(theme.palette.primary.light, 0)} 70%)`,
                    zIndex: 0
                  }}
                />
                
                {/* Header */}
                <Box 
                  sx={{ 
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    p: { xs: 2, md: 3 },
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 40, md: 48 },
                      height: { xs: 40, md: 48 },
                      borderRadius: '12px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                    }}
                  >
                    <BusinessIcon color="primary" sx={{ fontSize: { xs: 22, md: 26 } }} />
                  </Box>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      fontSize: { xs: '1.25rem', md: '1.5rem' }
                    }}
                  >
                    Corporate Headquarters
                  </Typography>
                </Box>
                
                {/* Content */}
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                  <motion.div variants={fadeInUp}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      mb: { xs: 2, md: 3 },
                      pb: { xs: 2, md: 3 },
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`
                    }}>
                      <Box 
                        sx={{ 
                          width: { xs: 32, md: 38 }, 
                          height: { xs: 32, md: 38 }, 
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          mr: { xs: 2, md: 2.5 },
                          mt: 0.5,
                          color: theme.palette.primary.main,
                          flexShrink: 0,
                        }}
                      >
                        <LocationOnIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
                      </Box>
                      <Box>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5, color: theme.palette.text.primary }}>
                          The United Travel
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                          F-8 Plot No. 347/2, Third Floor, Surbhi Complex<br />
                          Sector - 22, Gandhinagar - 382021
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                  
                  <motion.div variants={fadeInUp}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      mb: { xs: 2, md: 3 },
                      pb: { xs: 2, md: 3 },
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`
                    }}>
                      <Box 
                        sx={{ 
                          width: { xs: 32, md: 38 }, 
                          height: { xs: 32, md: 38 }, 
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          mr: { xs: 2, md: 2.5 },
                          mt: 0.5,
                          color: theme.palette.primary.main,
                          flexShrink: 0,
                        }}
                      >
                        <PhoneIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
                      </Box>
                      <Box>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5, color: theme.palette.text.primary }}>
                          Phone
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                          +91 6351003667
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                  
                  <motion.div variants={fadeInUp}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      mb: { xs: 2, md: 3 },
                      pb: { xs: 2, md: 3 },
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`
                    }}>
                      <Box 
                        sx={{ 
                          width: { xs: 32, md: 38 }, 
                          height: { xs: 32, md: 38 }, 
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          mr: { xs: 2, md: 2.5 },
                          mt: 0.5,
                          color: theme.palette.primary.main,
                          flexShrink: 0,
                        }}
                      >
                        <EmailIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
                      </Box>
                      <Box>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5, color: theme.palette.text.primary }}>
                          Email
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, wordBreak: 'break-word' }}>
                          contact.theunitedtravels@gmail.com
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                  
                  <motion.div variants={fadeInUp}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Box 
                        sx={{ 
                          width: { xs: 32, md: 38 }, 
                          height: { xs: 32, md: 38 }, 
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          mr: { xs: 2, md: 2.5 },
                          mt: 0.5,
                          color: theme.palette.primary.main,
                          flexShrink: 0,
                        }}
                      >
                        <AccessTimeIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
                      </Box>
                      <Box>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5, color: theme.palette.text.primary }}>
                          Business Hours
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                          Monday - Sunday: 10:00 AM - 6:00 PM
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                </Box>
              </Paper>
              
              {/* Regional Offices Card */}
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: { xs: 2, md: 4 },
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
                  position: 'relative',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
                }}
              >
                {/* Decorative element */}
                <Box 
                  sx={{ 
                    position: 'absolute',
                    bottom: -80,
                    left: -80,
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(theme.palette.secondary.light, 0.08)} 0%, ${alpha(theme.palette.secondary.light, 0)} 70%)`,
                    zIndex: 0
                  }}
                />
                
                {/* Header */}
                <Box 
                  sx={{ 
                    bgcolor: alpha(theme.palette.secondary.main, 0.03),
                    p: { xs: 2, md: 3 },
                    borderBottom: `1px solid ${alpha(theme.palette.secondary.main, 0.08)}`,
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 40, md: 48 },
                      height: { xs: 40, md: 48 },
                      borderRadius: '12px',
                      backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.2)}`,
                    }}
                  >
                    <LocationOnIcon sx={{ fontSize: { xs: 22, md: 26 }, color: theme.palette.secondary.main }} />
                  </Box>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700,
                      color: theme.palette.secondary.main,
                      fontSize: { xs: '1.25rem', md: '1.5rem' }
                    }}
                  >
                    Regional Offices
                  </Typography>
                </Box>
                
                {/* Content */}
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                  <GridContainer spacing={2}>
                    <GridItem xs={12}>
                      <motion.div 
                        variants={fadeInUp}
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: { xs: 2, md: 2.5 },
                            borderRadius: { xs: 2, md: 3 },
                            border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              boxShadow: `0 8px 20px ${alpha(theme.palette.secondary.main, 0.15)}`,
                              borderColor: alpha(theme.palette.secondary.main, 0.2),
                              backgroundColor: alpha(theme.palette.secondary.main, 0.02),
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <Box 
                              sx={{ 
                                width: { xs: 28, md: 32 }, 
                                height: { xs: 28, md: 32 }, 
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: alpha(theme.palette.success.main, 0.15),
                                mr: 2,
                                color: theme.palette.success.dark,
                              }}
                            >
                              <LocationOnIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
                            </Box>
                            <Typography variant="h6" fontWeight={600} color="text.primary" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>
                              Gandhinagar Office
                            </Typography>
                          </Box>
                          <Box 
                            sx={{ 
                              pl: { xs: 5.5, md: 6.5 }, 
                              borderLeft: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                              ml: { xs: 1, md: 1.2 }
                            }}
                          >
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                              F-8 Plot No. 347/2, Third Floor<br />
                              Surbhi Complex, Sector - 22<br />
                              Gandhinagar - 382021<br />
                              +91 6351003667
                            </Typography>
                          </Box>
                        </Paper>
                      </motion.div>
                    </GridItem>
                    
                    {/* <GridItem xs={12}>
                      <motion.div 
                        variants={fadeInUp}
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: { xs: 2, md: 2.5 },
                            borderRadius: { xs: 2, md: 3 },
                            border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              boxShadow: `0 8px 20px ${alpha(theme.palette.secondary.main, 0.15)}`,
                              borderColor: alpha(theme.palette.secondary.main, 0.2),
                              backgroundColor: alpha(theme.palette.secondary.main, 0.02),
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <Box 
                              sx={{ 
                                width: { xs: 28, md: 32 }, 
                                height: { xs: 28, md: 32 }, 
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: alpha(theme.palette.info.main, 0.15),
                                mr: 2,
                                color: theme.palette.info.dark,
                              }}
                            >
                              <LocationOnIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
                            </Box>
                            <Typography variant="h6" fontWeight={600} color="text.primary" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>
                              Gandhinagar Branch
                            </Typography>
                          </Box>
                          <Box 
                            sx={{ 
                              pl: { xs: 5.5, md: 6.5 }, 
                              borderLeft: `2px solid ${alpha(theme.palette.info.main, 0.3)}`,
                              ml: { xs: 1, md: 1.2 }
                            }}
                          >
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                              F-8 Plot No. 347/2, Third Floor<br />
                              Surbhi Complex, Sector - 22<br />
                              Gandhinagar - 382021<br />
                              +91 6351003667
                            </Typography>
                          </Box>
                        </Paper>
                      </motion.div>
                    </GridItem> */}
                    
                    {/* <GridItem xs={12}>
                      <motion.div 
                        variants={fadeInUp}
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: { xs: 2, md: 2.5 },
                            borderRadius: { xs: 2, md: 3 },
                            border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              boxShadow: `0 8px 20px ${alpha(theme.palette.secondary.main, 0.15)}`,
                              borderColor: alpha(theme.palette.secondary.main, 0.2),
                              backgroundColor: alpha(theme.palette.secondary.main, 0.02),
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <Box 
                              sx={{ 
                                width: { xs: 28, md: 32 }, 
                                height: { xs: 28, md: 32 }, 
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: alpha(theme.palette.warning.main, 0.15),
                                mr: 2,
                                color: theme.palette.warning.dark,
                              }}
                            >
                              <LocationOnIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
                            </Box>
                            <Typography variant="h6" fontWeight={600} color="text.primary" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>
                              Gandhinagar Service Center
                            </Typography>
                          </Box>
                          <Box 
                            sx={{ 
                              pl: { xs: 5.5, md: 6.5 }, 
                              borderLeft: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                              ml: { xs: 1, md: 1.2 }
                            }}
                          >
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                              F-8 Plot No. 347/2, Third Floor<br />
                              Surbhi Complex, Sector - 22<br />
                              Gandhinagar - 382021<br />
                              +91 6351003667
                            </Typography>
                          </Box>
                        </Paper>
                      </motion.div>
                    </GridItem> */}
                  </GridContainer>
                </Box>
              </Paper>
            </motion.div>
          </GridItem>
        </GridContainer>
        
        {/* Map Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Box sx={{ mt: { xs: 5, md: 8 }, position: 'relative' }}>
            <Box sx={{ 
              position: 'absolute', 
              top: { xs: -40, sm: -50, md: -60 }, 
              width: '100%', 
              zIndex: 1, 
              px: { xs: 1, md: 2 }
            }}>
              <Paper
                elevation={0}
                sx={{
                  maxWidth: 700,
                  mx: 'auto',
                  borderRadius: { xs: 2, md: 3 },
                  p: { xs: 2, md: 3 },
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  backgroundColor: alpha(theme.palette.secondary.main, 0.9),
                  color: 'white',
                  boxShadow: `0 8px 32px ${alpha(theme.palette.secondary.main, 0.3)}`,
                }}
              >
                <Box 
                  sx={{ 
                    width: { xs: 36, md: 40 }, 
                    height: { xs: 36, md: 40 }, 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: alpha(theme.palette.common.white, 0.2),
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 2, sm: 0 },
                    flexShrink: 0,
                  }}
                >
                  <LocationOnIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
                </Box>
                <Box sx={{ 
                  flex: 1,
                  mb: { xs: 2, sm: 0 },
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 700, 
                    mb: 0.5,
                    fontSize: { xs: '1.1rem', md: '1.25rem' }
                  }}>
                    Visit Our Office
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Discover how our team can help you find the perfect transportation solution for your needs
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ 
                    ml: { sm: 2 },
                    px: { xs: 2, md: 3 },
                    py: { xs: 1, md: 1.5 },
                    height: { xs: 40, md: 44 },
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    flexShrink: 0,
                    bgcolor: 'white',
                    color: theme.palette.secondary.main,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.common.white, 0.9),
                    },
                    width: { xs: '100%', sm: 'auto' }
                  }}
                  endIcon={<ArrowForwardIcon />}
                  component={RouterLink}
                  to="https://maps.app.goo.gl/BUT83Z5K4YDNHyPZ6"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get Directions
                </Button>
              </Paper>
            </Box>

            <Paper 
              elevation={0} 
              sx={{ 
                borderRadius: { xs: 2, md: 4 },
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                boxShadow: '0 20px 60px rgba(0,0,0,0.05)',
                mt: { xs: 2, md: 0 }
              }}
            >
              <Box
                component="iframe"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d344.4092499527352!2d72.65273402523776!3d23.234020678917727!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395c2b00251267a7%3A0xee16a215ed87f57b!2sSurbhi%20complex!5e0!3m2!1sen!2sin!4v1761979603671!5m2!1sen!2sin"
                sx={{
                  height: { xs: 300, md: 450 },
                  width: '100%',
                  border: 0,
                  display: 'block',
                }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="The United Travel Location - Gandhinagar"
              />
            </Paper>
          </Box>
        </motion.div>

        {/* FAQ Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Box sx={{ 
            mt: { xs: 6, md: 8 }, 
            mb: { xs: 2, md: 0 },
            textAlign: 'center',
            px: { xs: 2, md: 0 }
          }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '1.75rem', md: '2.125rem' }
              }}
            >
              Have More Questions?
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                maxWidth: 600,
                mx: 'auto',
                mb: { xs: 3, md: 4 },
                fontSize: { xs: '0.95rem', md: '1rem' }
              }}
            >
              Check our frequently asked questions for quick answers to common inquiries about our services, booking process, and policies.
            </Typography>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                variant="contained"
                color="secondary"
                size={isMobile ? "medium" : "large"}
                component={RouterLink}
                to="/faqs"
                sx={{
                  px: { xs: 3, md: 4 },
                  py: { xs: 1, md: 1.5 },
                  borderRadius: 30,
                  boxShadow: `0 8px 25px ${alpha(theme.palette.secondary.main, 0.3)}`,
                  width: { xs: '100%', sm: 'auto' },
                  maxWidth: { xs: '80%', sm: 'none' },
                  mx: 'auto'
                }}
              >
                View FAQs
              </Button>
            </motion.div>
          </Box>
        </motion.div>
      </Container>
      
      {/* Success/Error Messages */}
      <Snackbar 
        open={showSuccess} 
        autoHideDuration={6000} 
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success" 
          variant="filled"
          elevation={6}
          sx={{ width: '100%' }}
        >
          Thank you for your message! Our team will contact you shortly.
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={showError} 
        autoHideDuration={6000} 
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowError(false)} 
          severity="error" 
          variant="filled"
          elevation={6}
          sx={{ width: '100%' }}
        >
          There was an error sending your message. Please try again.
        </Alert>
      </Snackbar>
    </Box>
    </>
  );
};

export default ContactPage; 